import type { SignalingMessage } from '@/transport/protocol';

/**
 * Обгортка над медіазʼєднанням: створення, обмін описами, буферизація
 * кандидатів і повне звільнення.
 *
 * Тут немає ані сокета, ані станів інтерфейсу — лише те, що стосується
 * одного `RTCPeerConnection`. Кожна перебудова зʼєднання створює НОВИЙ
 * екземпляр, а старий звільняється цілком: саме змішування цих двох
 * життєвих циклів колись давало паралельні спроби й витік зʼєднань.
 */

export type PeerConnectionFactory = (config: RTCConfiguration) => RTCPeerConnection;

const defaultFactory: PeerConnectionFactory = (config) => new RTCPeerConnection(config);

export interface PeerSessionHandlers {
  onLocalDescription: (description: RTCSessionDescriptionInit) => void;
  onLocalCandidate: (candidate: RTCIceCandidateInit) => void;
  onRemoteTrack: (stream: MediaStream) => void;
  /** Зʼєднання деградувало. Це сигнал про втрату, а не команда відновлювати. */
  onConnectionLost: () => void;
  onConnected?: () => void;
}

export class PeerSession {
  private pc: RTCPeerConnection | null;
  /**
   * Кандидати, що прийшли до встановлення віддаленого опису. Без буфера вони
   * просто губляться, і дзвінок «майже встає» — найгірший вид збою.
   */
  private pendingCandidates: RTCIceCandidateInit[] = [];
  private remoteDescriptionSet = false;
  private disposed = false;
  private readonly senders = new Map<string, RTCRtpSender>();

  constructor(
    configuration: RTCConfiguration,
    private readonly handlers: PeerSessionHandlers,
    factory: PeerConnectionFactory = defaultFactory,
  ) {
    const pc = factory(configuration);
    this.pc = pc;

    pc.onicecandidate = (event): void => {
      if (event.candidate) {
        this.handlers.onLocalCandidate(event.candidate.toJSON());
      }
    };

    pc.ontrack = (event): void => {
      const stream = event.streams[0];
      if (stream) {
        this.handlers.onRemoteTrack(stream);
      }
    };

    pc.onconnectionstatechange = (): void => {
      if (this.disposed) return;
      if (pc.connectionState === 'connected') {
        this.handlers.onConnected?.();
      }
      if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        this.handlers.onConnectionLost();
      }
    };

    pc.oniceconnectionstatechange = (): void => {
      if (this.disposed) return;
      if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'disconnected') {
        this.handlers.onConnectionLost();
      }
    };
  }

  get connectionState(): RTCPeerConnectionState | 'disposed' {
    return this.pc?.connectionState ?? 'disposed';
  }

  addLocalStream(stream: MediaStream): void {
    const pc = this.pc;
    if (!pc) return;
    for (const track of stream.getTracks()) {
      this.senders.set(track.kind, pc.addTrack(track, stream));
    }
  }

  /** Заміна треку без перестворення зʼєднання — камера, демонстрація екрана. */
  async replaceTrack(kind: string, track: MediaStreamTrack | null): Promise<void> {
    const sender = this.senders.get(kind);
    if (!sender) return;
    await sender.replaceTrack(track);
  }

  async createOffer(): Promise<void> {
    const pc = this.pc;
    if (!pc) return;
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    this.handlers.onLocalDescription(offer);
  }

  async acceptOffer(description: RTCSessionDescriptionInit): Promise<void> {
    const pc = this.pc;
    if (!pc) return;
    await pc.setRemoteDescription(description);
    await this.drainCandidates();
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    this.handlers.onLocalDescription(answer);
  }

  async acceptAnswer(description: RTCSessionDescriptionInit): Promise<void> {
    const pc = this.pc;
    if (!pc) return;
    await pc.setRemoteDescription(description);
    await this.drainCandidates();
  }

  async addRemoteCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    const pc = this.pc;
    if (!pc) return;
    if (!this.remoteDescriptionSet) {
      this.pendingCandidates.push(candidate);
      return;
    }
    await pc.addIceCandidate(candidate);
  }

  private async drainCandidates(): Promise<void> {
    this.remoteDescriptionSet = true;
    const pc = this.pc;
    if (!pc) return;
    const queued = this.pendingCandidates;
    this.pendingCandidates = [];
    for (const candidate of queued) {
      try {
        await pc.addIceCandidate(candidate);
      } catch {
        // Один непридатний кандидат не має зривати решту обміну.
      }
    }
  }

  /**
   * Звільняє зʼєднання цілком. Кожен крок ізольований: помилка на одному
   * не має лишати наступні невиконаними.
   */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    const pc = this.pc;
    this.pc = null;
    this.pendingCandidates = [];
    this.senders.clear();
    if (!pc) return;

    // Знімаємо слухачі ДО закриття: інакше закриття саме викличе
    // onconnectionstatechange і подія від мертвого зʼєднання поїде в стан.
    try {
      pc.onicecandidate = null;
      pc.ontrack = null;
      pc.onconnectionstatechange = null;
      pc.oniceconnectionstatechange = null;
    } catch {
      // Слухачі могли бути вже зняті платформою.
    }

    try {
      pc.close();
    } catch {
      // Закриття вже закритого зʼєднання не має зривати завершення дзвінка.
    }
  }
}

/** Витягує опис/кандидата з розібраного повідомлення сигналінгу. */
export function describeIncoming(
  message: SignalingMessage,
):
  | { kind: 'offer'; description: RTCSessionDescriptionInit }
  | { kind: 'answer'; description: RTCSessionDescriptionInit }
  | { kind: 'candidate'; candidate: RTCIceCandidateInit }
  | null {
  switch (message.kind) {
    case 'offer':
      return { kind: 'offer', description: message.offer };
    case 'answer':
      return { kind: 'answer', description: message.answer };
    case 'candidate': {
      // Схема допускає null і undefined там, де RTCIceCandidateInit чекає
      // або значення, або null. Нормалізуємо, а не приводимо типом.
      const { candidate, sdpMid, sdpMLineIndex, usernameFragment } = message.candidate;
      return {
        kind: 'candidate',
        candidate: {
          candidate,
          sdpMid: sdpMid ?? null,
          sdpMLineIndex: sdpMLineIndex ?? null,
          ...(usernameFragment === null || usernameFragment === undefined
            ? {}
            : { usernameFragment }),
        },
      };
    }
    default:
      return null;
  }
}
